import moment from "moment";
import { CLOSING_DAY_EOM } from "@/tenant";

// 入力チェックリスト（Excel）の生成（SPEC-037）
// 旧APIのサーバ生成（EntryCheckService.makeExcel）と同じ構成をブラウザ上で作る。

// 締め日の既定値（mykk: 毎月20日締め。前月21日〜当月20日）。会社ごとの締め日は tenant.js で定義する（SPEC-039）
const DEFAULT_CLOSING_DAY = 20;

// 日付列より前の固定列（CD・名称・グループ）
const HEAD_COLUMNS = ["CD", "名称", "グループ"];

// 土曜・日曜の日付セルの背景色（旧Excelの SKY_BLUE / LAVENDER 相当）
const SATURDAY_COLOR = "FFB3E5FC";
const SUNDAY_COLOR = "FFE1BEE7";

/**
 * 検索日の年月を締め月として、締め期間を求める（SPEC-039 BR-E1: 締め日は会社ごと）。
 * 締め月は検索日の年月（締め日より後の日でも翌月にしない。旧仕様どおり）。
 * @param {string} dateStr 検索日（YYYY-MM-DD）
 * @param {number|string} closingDay 締め日（1〜28 の日付、または月末締めの "EOM"。省略時は 20）
 * @returns {{fromDate: string, toDate: string, days: number, year: number, month: number}}
 *   20日締め: 前月21日〜当月20日、月末締め: 当月1日〜月末（いずれも YYYY-MM-DD）、days: 期間の日数
 */
function calcClosingPeriod(dateStr, closingDay) {
  const base = moment(dateStr, "YYYY-MM-DD");
  if (closingDay === CLOSING_DAY_EOM) {
    // 月末締め（暦月）: 当月1日〜月末
    const fromEom = base.clone().startOf("month");
    const toEom = base.clone().endOf("month").startOf("day");
    return {
      fromDate: fromEom.format("YYYY-MM-DD"),
      toDate: toEom.format("YYYY-MM-DD"),
      days: toEom.diff(fromEom, "days") + 1,
      year: toEom.year(),
      month: toEom.month() + 1,
    };
  }
  const day = Number(closingDay) || DEFAULT_CLOSING_DAY;
  const to = moment({ year: base.year(), month: base.month(), day: day });
  const from = to.clone().subtract(1, "months").add(1, "days");

  return {
    fromDate: from.format("YYYY-MM-DD"),
    toDate: to.format("YYYY-MM-DD"),
    days: to.diff(from, "days") + 1,
    year: to.year(),
    month: to.month() + 1,
  };
}

/**
 * 入力チェックAPIの応答から、Excel の行データ（担当者ごとの日報有無）を作る。
 * 並びは画面の一覧と同じ（グループ順→グループ長→その他）。
 * @param {object} data 入力チェックAPIの応答（groups / entries）
 * @returns {{dates: string[], rows: {staffCd: string, name: string, groupName: string, entries: boolean[]}[]}}
 */
function buildRows(data) {
  const entries = data.entries || [];
  const dates = entries.map((e) => e.date);

  // 日付ごとに日報のある担当者CDの集合を作る
  const staffSets = entries.map(
    (e) => new Set((e.reports || []).map((r) => r.staffCd))
  );

  const rows = [];
  (data.groups || []).forEach((g) => {
    (g.staffs || []).forEach((s) => {
      rows.push({
        staffCd: s.staffCd,
        name: s.name,
        groupName: g.group.groupName,
        entries: staffSets.map((set) => set.has(s.staffCd)),
      });
    });
  });

  return { dates, rows };
}

/**
 * 入力チェックリストのワークブックを作る。
 * 1行目: 空×3＋各日付の「YYYY年M月」、2行目: CD・名称・グループ＋各日付の「日」（土曜=水色、日曜=ラベンダー）、
 * 3行目以降: 担当者ごとに日報がある日は「○」。
 * @param {object} ExcelJS 動的に読み込んだ ExcelJS モジュール
 * @param {object} data 入力チェックAPIの応答（groups / entries）
 * @returns {object} ExcelJS の Workbook
 */
function buildEntryCheckWorkbook(ExcelJS, data) {
  const { dates, rows } = buildRows(data);

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("入力チェック");

  // ヘッダ1行目（年月）
  const monthRow = sheet.addRow(
    ["", "", ""].concat(
      dates.map((d) => moment(d, "YYYY-MM-DD").format("YYYY年M月"))
    )
  );
  // ヘッダ2行目（固定列名＋日）
  const dayRow = sheet.addRow(
    HEAD_COLUMNS.concat(dates.map((d) => moment(d, "YYYY-MM-DD").date()))
  );
  [monthRow, dayRow].forEach((r) => {
    r.font = { bold: true };
  });

  // 土日の日付セルに色を付ける
  dates.forEach((d, i) => {
    const dow = moment(d, "YYYY-MM-DD").day();
    const color = dow === 6 ? SATURDAY_COLOR : dow === 0 ? SUNDAY_COLOR : null;
    if (color) {
      dayRow.getCell(HEAD_COLUMNS.length + i + 1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: color },
      };
    }
  });

  // 明細（日報がある日は「○」）
  rows.forEach((row) => {
    sheet.addRow(
      [row.staffCd, row.name, row.groupName].concat(
        row.entries.map((exists) => (exists ? "○" : ""))
      )
    );
  });

  // 列幅（固定列は内容に合わせ、日付列は狭く）
  const fixedWidths = [6, 14, 14];
  rows.forEach((row) => {
    fixedWidths[1] = Math.max(fixedWidths[1], (row.name || "").length * 2 + 2);
    fixedWidths[2] = Math.max(fixedWidths[2], (row.groupName || "").length * 2 + 2);
  });
  sheet.columns.forEach((col, i) => {
    col.width = i < HEAD_COLUMNS.length ? fixedWidths[i] : 11;
  });

  return workbook;
}

/**
 * 出力ファイル名を作る（日時はブラウザの現地時刻）。
 * @param {Date|moment.Moment} now 現在日時
 * @returns {string} 入力チェックリスト_yyyyMMddHHmmss.xlsx
 */
function makeFileName(now) {
  return "入力チェックリスト_" + moment(now).format("YYYYMMDDHHmmss") + ".xlsx";
}

export default {
  calcClosingPeriod,
  buildRows,
  buildEntryCheckWorkbook,
  makeFileName,
};
